import { useCallback, useEffect, useState } from 'react';
import type {
  SchedulePrintCourtRange,
  SchedulePrintMapOrientation,
} from './schedulePrintModel';

const STORAGE_KEY = 'raqt-schedule-print-map-prefs';

type StoredPrefs = {
  mapOrientation?: SchedulePrintMapOrientation;
  courtRanges?: SchedulePrintCourtRange[] | null;
};

const readStoredPrefs = (): StoredPrefs => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as StoredPrefs;
  } catch {
    return {};
  }
};

export function useSchedulePrintMapPrefs() {
  const [mapOrientation, setMapOrientation] = useState<SchedulePrintMapOrientation>('timeOnX');
  const [courtRanges, setCourtRanges] = useState<SchedulePrintCourtRange[] | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const stored = readStoredPrefs();
    if (stored.mapOrientation) setMapOrientation(stored.mapOrientation);
    if (stored.courtRanges !== undefined) setCourtRanges(stored.courtRanges);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const payload: StoredPrefs = { mapOrientation, courtRanges };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [courtRanges, hydrated, mapOrientation]);

  const resetMapPrefs = useCallback(() => {
    setMapOrientation('timeOnX');
    setCourtRanges(null);
  }, []);

  return {
    mapOrientation,
    setMapOrientation,
    courtRanges,
    setCourtRanges,
    resetMapPrefs,
  };
}
